import { FC } from 'react';
import { Flex, Icon, Text, Tooltip } from '@chakra-ui/react';
import { IconType } from 'react-icons';
import {
  BsFileEarmark,
  BsFileEarmarkZip,
  BsFiletypeCsv,
  BsFiletypePdf,
  BsFiletypeTxt,
  BsFiletypeXlsx,
} from 'react-icons/bs';

import { Link } from 'react-router-dom';

import {
  Directory,
  File,
  FileBrowserStorageObjectFragment,
} from '../../graphql/generated';
import { isFile } from '../../graphql/utils/utils';
import { isDirectory } from '../../graphql/utils/utils';
import { navigateToDirectory } from '../../pages/file-browser-page/file-browser-page';
import { navigateToFileDetailsRoute } from '../../pages/file-details-page/file-details-page';

import { FolderIcon } from '../../ui/icons/folder';
import { DltStatus } from '../file/dlt-status/dlt-status';

type Props = {
  object: FileBrowserStorageObjectFragment;
  enableTooltip?: boolean;
  asLink?: boolean;
};

export const generateDirectoryLink = (directory: Directory): string =>
  navigateToDirectory({
    cedantId: directory.cedant,
    directoryId: directory.id,
  });

export const generateFileLink = (file: Pick<File, 'id' | 'cedant'>): string =>
  navigateToFileDetailsRoute({
    cedantId: file.cedant,
    fileId: file.id,
  });

/**
 * Folder display name overrides — visual only, does not affect actual paths.
 * Scoped to specific cedant + folder path combinations.
 */
const FOLDER_DISPLAY_OVERRIDES: { cedant: string; path: string; folderName: string; displayName: string }[] = [
  { cedant: 'ALLIANZUS202112', path: 'ToAllianz-US/Investments/', folderName: 'Investments', displayName: 'Investments - Do Not Use' },
];

const getDisplayName = (name: string, cedant?: string, path?: string) => {
  const baseName = name.split('/').filter(Boolean).pop() || name;
  if (cedant && path) {
    const override = FOLDER_DISPLAY_OVERRIDES.find(
      (o) => o.cedant.toLowerCase() === cedant.toLowerCase()
        && o.folderName === baseName
        && o.path === path
    );
    if (override) return override.displayName;
  }
  return baseName;
};

type LabelProps = {
  id: string;
  displayName: string;
  tooltipLabel?: string;
  enableTooltip?: boolean;
};

function Label({ enableTooltip, id, displayName, tooltipLabel }: LabelProps) {
  if (enableTooltip && tooltipLabel) {
    return (
      <Tooltip
        placement="start-start"
        label={tooltipLabel}
      >
        <Text id={id} isTruncated>
          {displayName}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Text id={id} isTruncated>
      {displayName}
    </Text>
  );
}

export const getContentTypeIcon = (
  file: Pick<File, 'contentType'>
): IconType => {
  const contentTypes = [
    {
      contentType:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      icon: BsFiletypeXlsx,
    },
    { contentType: 'application/pdf', icon: BsFiletypePdf },
    { contentType: 'text/plain', icon: BsFiletypeTxt },
    { contentType: 'text/csv', icon: BsFiletypeCsv },
    { contentType: 'application/zip', icon: BsFileEarmarkZip },
  ];

  const contentType = contentTypes.find((a) =>
    file.contentType.includes(a.contentType)
  );

  return contentType ? contentType.icon : BsFileEarmark;
};

export const Name: FC<Props> = ({
  object,
  enableTooltip = false,
  asLink = true,
}) => {
  if (isDirectory(object)) {
    const dirDisplayName = getDisplayName(object.name, object.cedant, object.path);
    const tooltipLabel = enableTooltip ? `${object.cedant}/${object.path}${object.name}` : undefined;
    return (
      <Flex
        as={asLink ? Link : undefined}
        isTruncated
        gridGap="3"
        alignItems="center"
        data-file-type="directory-link"
        to={generateDirectoryLink(object)}
      >
        <FolderIcon />
        <Label id={object.id} displayName={dirDisplayName} enableTooltip={enableTooltip} tooltipLabel={tooltipLabel} />
      </Flex>
    );
  }

  if (isFile(object)) {
    const fileDisplayName = getDisplayName(object.name);
    const tooltipLabel = enableTooltip ? `${object.cedant}/${object.path}${object.name}` : undefined;
    return (
      <Flex
        as={asLink ? Link : undefined}
        isTruncated
        gridGap="3"
        alignItems="center"
        data-file-type="file-link"
        to={generateFileLink(object)}
      >
        <Icon
          as={getContentTypeIcon(object)}
          fontSize="2rem"
          color="gray.500"
        />
        <Label id={object.id} displayName={fileDisplayName} enableTooltip={enableTooltip} tooltipLabel={tooltipLabel} />
        <DltStatus object={object} />
      </Flex>
    );
  }

  return null;
};
